// ============================================================
// Moteur PUR de la BANDE DE GLISSÉ — AUCUNE dépendance Angular.
// Testé sans TestBed : hr-drag-band.spec.ts.
// ============================================================
//
// Déplacer une ligne d'un planning, c'est déplacer des HEURES. Une vignette réduite à côté du
// curseur ne dit pas ce geste-là : on croit déplacer une personne, un nom, une étiquette. Ce qui
// suit la souris doit donc être la LIGNE ELLE-MÊME, à la taille de la grille et sur ses colonnes,
// qui se décolle et monte ou descend jusqu'à sa nouvelle place.
//
// Le CDK ancre la racine de son aperçu sur le POINT DE SAISIE (`position: fixed`, qu'il impose).
// Tout ce qui suit est donc exprimé RELATIVEMENT AU CURSEUR : la bande se replace à partir de cette
// ancre, et retombe pile sur la ligne d'origine à l'instant où on la soulève.
//
// La géométrie se relève AU CLIC, jamais au démarrage du glissé : le CDK fabrique son aperçu au
// premier mouvement et le rend une seule fois, avec les valeurs qu'il trouve alors. Relevée plus
// tard, la bande naîtrait à zéro et sauterait à sa place au mouvement suivant.

/// Un rectangle à l'écran, réduit à ce qu'on en lit. `DOMRect` en tient lieu ; l'interface existe
/// pour que la géométrie se calcule (et se teste) sans navigateur.
export interface ScreenRect {
	left: number;
	top: number;
	width: number;
	height: number;
}

/// La bande telle qu'elle se dessine, en pixels et relativement au curseur.
export interface DragBandVM {
	left: number;			// bord gauche de la découpe
	top: number;			// haut de la ligne
	width: number;		// largeur VISIBLE : la grille défile horizontalement sur les écrans étroits
	height: number;
	innerLeft: number;											// décalage des cases dans la découpe (négatif si la grille est défilée)
	cells: { left: number; width: number }[];	// une case par colonne relevée, dans l'ordre d'affichage
}

export const EMPTY_DRAG_BAND: DragBandVM = { left: 0, top: 0, width: 0, height: 0, innerLeft: 0, cells: [] };

/// La géométrie de la bande à partir des rectangles relevés sur la ligne : ses cases, la fenêtre
/// visible de la grille (`clip`, qui défile horizontalement), et le point de saisie.
export function dragBandGeometry(cells: ScreenRect[], clip: ScreenRect | null, pickupX: number, pickupY: number): DragBandVM {
	if (cells.length === 0) return EMPTY_DRAG_BAND;
	const first = cells[0];
	const last = cells[cells.length - 1];
	// La bande ne déborde jamais de la zone où la grille est visible : au-delà, elle flotterait
	// au-dessus du reste de la page sans rien vouloir dire.
	const left = clip ? Math.max(first.left, clip.left) : first.left;
	const right = clip ? Math.min(last.left + last.width, clip.left + clip.width) : last.left + last.width;
	return {
		left: left - pickupX,
		top: first.top - pickupY,
		width: Math.max(0, right - left),
		height: first.height,
		innerLeft: first.left - left,
		cells: cells.map(c => ({ left: c.left - first.left, width: c.width })),
	};
}

/// La bande d'une ligne du DOM : les cases désignées par `selector`, découpées sur le conteneur
/// défilant qui les porte. Rend `null` si le clic n'est pas parti d'une ligne — l'appelant garde
/// alors la bande précédente plutôt que d'en fabriquer une vide.
export function dragBandOfRow(from: EventTarget | null, selector: string, pickupX: number, pickupY: number): DragBandVM | null {
	const line = (from as HTMLElement | null)?.closest?.('tr');
	if (!line) return null;
	const cells = Array.from(line.querySelectorAll<HTMLElement>(selector)).map(c => c.getBoundingClientRect());
	const scroller = line.closest('.grid-scroll');
	return dragBandGeometry(cells, scroller?.getBoundingClientRect() ?? null, pickupX, pickupY);
}
